import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiHome, FiActivity, FiUsers, FiDollarSign, FiCreditCard,
  FiFileText, FiHeart, FiAlertCircle, FiSettings, FiShield, FiDatabase,
  FiTrendingUp, FiAlertTriangle, FiPackage, FiArchive, FiUserCheck, FiFolder,
  FiCheckSquare, FiKey, FiGift, FiMail, FiCpu, FiZap, FiLayout, FiSend, FiStar, FiInbox, FiX,
  FiArrowLeft
} from 'react-icons/fi';

interface AdminSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  /**
   * Controls the mobile drawer. Ignored on md+ where the sidebar is
   * always visible. Defaults to false so nothing breaks if a caller
   * forgets to wire it up.
   */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Pruned sidebar — 4 dead entries hidden (KYC, Social Playbook, Platform
// Tasks, CTO Tasks). Their case blocks in AdminDashboard's switch stay
// wired so any deep link like /admin#kyc still resolves, but the entries
// no longer clutter the sidebar. Flip `show: false` → `true` to bring
// any one back.
const menuItems = [
  { id: 'overview', label: 'Dashboard', icon: FiHome },
  { id: 'growth', label: 'Growth', icon: FiTrendingUp },
  { id: 'twitter-growth', label: 'Twitter Growth', icon: FiZap },
  { id: 'social-playbook', label: 'Social Playbook', icon: FiCheckSquare, show: false },
  { id: 'live-events', label: 'Live Events', icon: FiActivity },
  { id: 'tasks', label: 'Platform Tasks', icon: FiCheckSquare, show: false },
  { id: 'users', label: 'Users', icon: FiUsers },
  { id: 'kyc', label: 'KYC Submissions', icon: FiUserCheck, show: false },
  { id: 'subscriptions', label: 'Subscriptions', icon: FiCreditCard },
  { id: 'billing', label: 'Billing', icon: FiDollarSign },
  { id: 'company-invoices', label: 'Company Invoices', icon: FiFileText },
  { id: 'emails', label: 'Email Templates', icon: FiMail },
  { id: 'broadcast', label: 'Broadcast Email', icon: FiSend },
  { id: 'feedback', label: 'Growth Feedback', icon: FiInbox },
  { id: 'founding', label: 'Founding Members', icon: FiStar },
  { id: 'reengagement', label: 'Re-engagement', icon: FiZap },
  { id: 'affiliate-payouts', label: 'Affiliate Payouts', icon: FiDollarSign },
  { id: 'crypto', label: 'Crypto Invoices', icon: FiDatabase },
  { id: 'anomalies', label: 'Anomalies', icon: FiAlertTriangle },
  { id: 'products', label: 'Products', icon: FiPackage },
  { id: 'contracts', label: 'Contracts', icon: FiArchive },
  { id: 'data-rooms', label: 'Data Room', icon: FiFolder, isLink: true, href: '/data-rooms' },
  { id: 'cto-tasks',  label: 'CTO Tasks', icon: FiLayout, isLink: true, href: '/cto-tasks', show: false },
  { id: 'testimonials', label: 'Testimonials', icon: FiGift },
  { id: 'bugs', label: 'Bug Reports', icon: FiAlertCircle },
  { id: 'health', label: 'System Health', icon: FiHeart },
  { id: 'ai-settings', label: 'AI Model Config', icon: FiCpu },
  { id: 'landing-design', label: 'Site Design', icon: FiLayout },
  { id: 'payment-settings', label: 'Payment Config', icon: FiSettings },
  { id: 'quick-access', label: 'Quick Access', icon: FiKey, show: false },
].filter((item: any) => item.show !== false);

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeSection,
  onSectionChange,
  mobileOpen = false,
  onMobileClose,
}) => {
  const navigate = useNavigate();

  const handleItemClick = (item: typeof menuItems[0]) => {
    if ((item as any).isLink && (item as any).href) {
      navigate((item as any).href);
    } else {
      onSectionChange(item.id);
    }
    // Auto-close the mobile drawer on any pick so the user isn't left
    // scrolling behind a full-screen menu on their phone.
    onMobileClose?.();
  };

  // Shared inner content — used both by the always-visible desktop
  // sidebar and by the mobile drawer so we don't drift.
  const SidebarInner = (
    <>
      {/* Logo Header — includes a close button on mobile only. */}
      <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
            <FiShield className="text-white text-lg" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Admin</h1>
          </div>
        </div>
        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/70"
            aria-label="Close menu"
          >
            <FiX className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Back to user app — always pinned above the menu so it works
          on mobile drawer too (where the desktop-only AdminHeader link
          isn't visible). Uses full-anchor navigation, not react-router
          navigate(), so it also fires the Navbar/Footer conditionals
          on re-render. Auto-closes the mobile drawer on click. */}
      <div className="px-3 pt-3">
        <a
          href="/dashboard"
          onClick={() => onMobileClose?.()}
          className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/60 transition-colors text-sm font-medium"
        >
          <FiArrowLeft className="text-base" />
          <span>Back to app</span>
        </a>
      </div>

      {/* Navigation — V3 terminal palette:
          - default items: quiet slate-400, no chrome
          - hover: barely-there cyan tint + faint left-edge glow
          - active: solid left cyan border, cyan-tinted text, cyan icon
          - external-link chevron kept, but the loud pulsing cyan dot on
            the active row was removed (it fought with the border-l and
            added a second focal point on an already-highlighted row) */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <div className="space-y-0.5">
          {menuItems.map((item) => {
            const isActive = activeSection === item.id;
            const isLink = (item as any).isLink;
            return (
              <motion.button
                key={item.id}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleItemClick(item)}
                data-testid={`sidebar-${item.id}`}
                className={`w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-md text-sm transition-colors border-l-2 ${
                  isActive
                    ? 'bg-cyan-500/10 text-cyan-200 border-cyan-400 font-medium'
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.04] border-transparent hover:border-cyan-500/40'
                }`}
              >
                <item.icon
                  className={`text-base flex-shrink-0 ${
                    isActive ? 'text-cyan-400' : 'text-slate-500'
                  }`}
                />
                <span className="truncate">{item.label}</span>
                {isLink && (
                  <span className="ml-auto text-xs text-slate-500 font-mono">↗</span>
                )}
              </motion.button>
            );
          })}
        </div>
      </nav>

      {/* Footer — V3 terminal status chip, mono cyan chevron label so
          it reads as a system readout rather than a marketing tagline. */}
      <div className="px-4 py-3 border-t border-slate-800/70">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)] animate-pulse" />
          <span>▸ System online</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop — always visible, fixed at left. Hidden on mobile so
          the drawer variant takes over. */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 z-40 flex-col">
        {SidebarInner}
      </aside>

      {/* Mobile drawer — slides in from the left when mobileOpen is
          true, backed by a dimmed backdrop that closes on tap. Full
          height and covers the entire screen minus a small gap on the
          right so the user still perceives the content behind. */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onMobileClose}
          >
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="h-full w-72 max-w-[85vw] bg-slate-900/98 backdrop-blur-xl border-r border-slate-700/50 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {SidebarInner}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminSidebar;